import { getClosestElement } from '../Utility/Dom'

// Reusable in-page conditional visibility for form fields and wrappers.
// Set on a form element OR a wrapping <fieldset> via `fluidAdditionalAttributes`:
//   data-when-field: <other-field-identifier>
//   data-when-value: <comma-separated-values>
// For inputs the surrounding `[data-js="form-ctr"]` row is hidden and the input
// is disabled. For wrappers (e.g. <fieldset>) the wrapper itself is hidden and
// disabled — disabling a fieldset cascades to all child inputs, removing them
// from form submission and client-side validation.

interface ConditionalTarget {
  acceptedValues: string[]
  element: Toggleable
  hideTarget: HTMLElement
  watchedField: string
}
type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

type Toggleable = FormControl | HTMLFieldSetElement

class FormCondition {
  private targets: ConditionalTarget[] = []

  init(): void {
    const candidates = Array.from(document.querySelectorAll<HTMLElement>('[data-when-field]'))

    candidates.forEach((element: HTMLElement): void => {
      if (!this.isToggleable(element)) return

      const watchedField = element.dataset.whenField
      if (watchedField === undefined || watchedField === '') return

      this.targets.push({
        acceptedValues: this.parseValues(element.dataset.whenValue),
        element,
        hideTarget: this.resolveHideTarget(element),
        watchedField,
      })
    })

    if (this.targets.length === 0) return

    this.attachListeners()
    this.evaluate()
  }

  private attachListeners(): void {
    const watchedNames = new Set(this.targets.map((target: ConditionalTarget): string => target.watchedField))
    watchedNames.forEach((name: string): void => {
      const inputs = this.findInputsByIdentifier(name)
      inputs.forEach((input: HTMLElement): void => {
        input.addEventListener('change', (): void => this.evaluate())
        input.addEventListener('input', (): void => this.evaluate())
      })
    })
  }

  private evaluate(): void {
    this.targets.forEach((target: ConditionalTarget): void => {
      const watchedValue = this.getFieldValue(target.watchedField)
      const shouldShow = target.acceptedValues.includes(watchedValue)
      this.toggle(target, shouldShow)
    })
  }

  private findInputsByIdentifier(identifier: string): HTMLElement[] {
    const suffix = `[${identifier}]`
    return Array.from(document.querySelectorAll<HTMLElement>('[name][data-js="form-element"]')).filter((input: HTMLElement): boolean => {
      const name = (input as HTMLInputElement).name
      return name.endsWith(suffix)
    })
  }

  private getFieldValue(identifier: string): string {
    const inputs = this.findInputsByIdentifier(identifier) as (HTMLInputElement | HTMLSelectElement)[]
    if (inputs.length === 0) return ''

    const first = inputs[0]
    if (first instanceof HTMLInputElement && (first.type === 'radio' || first.type === 'checkbox')) {
      const checked = inputs.find((input): boolean => input instanceof HTMLInputElement && input.checked)
      return checked instanceof HTMLInputElement ? checked.value : ''
    }

    return first.value
  }

  private isToggleable(element: HTMLElement): element is Toggleable {
    return element instanceof HTMLInputElement
      || element instanceof HTMLSelectElement
      || element instanceof HTMLTextAreaElement
      || element instanceof HTMLFieldSetElement
  }

  private parseValues(raw: string | undefined): string[] {
    if (raw === undefined || raw === '') return []
    return raw.split(',').map((value: string): string => value.trim()).filter((value: string): boolean => value !== '')
  }

  private resolveHideTarget(element: Toggleable): HTMLElement {
    if (element instanceof HTMLFieldSetElement) {
      return element
    }
    return getClosestElement(element, 'form-ctr') ?? element
  }

  private toggle(target: ConditionalTarget, show: boolean): void {
    target.hideTarget.hidden = !show
    target.element.disabled = !show
    if (!show) {
      target.element.removeAttribute('aria-invalid')
    }
  }
}

const formCondition = new FormCondition()
formCondition.init()
